import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import axios from 'axios'

interface IngestionLastRun {
  success: boolean
  error?: string
  finishedAt: string
  snapshotsProcessed?: number
  filesProcessed?: number
  totalFilesFound?: number
}

interface IngestionStatus {
  status: 'idle' | 'running'
  isRunning: boolean
  lastProcessedCount: number
  lastRun?: IngestionLastRun | null
}

interface FileStats {
  totalS3Files: number
  processed: number
  processing: number
  failed: number
  pending: number
  totalInDatabase: number
}

interface CronJob {
  id: number
  name: string
  description: string
  cronExpression: string
  isEnabled: boolean
  lastRunAt?: string
  nextRunAt?: string
  runCount?: number
  lastStatus?: 'success' | 'failed' | 'running'
  lastError?: string
  createdAt: string
  updatedAt: string
}

export default function DataIngestionPage() {
  const [ingestionStatus, setIngestionStatus] = useState<IngestionStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [connectionTest, setConnectionTest] = useState<{
    success: boolean
    filesFound?: number
    error?: string
  } | null>(null)
  const [fileStats, setFileStats] = useState<FileStats | null>(null)
  const [showErrorDetails, setShowErrorDetails] = useState(false)
  const [cronJobs, setCronJobs] = useState<CronJob[]>([])
  const [editingCronJob, setEditingCronJob] = useState<CronJob | null>(null)
  const [previousIngestionStatus, setPreviousIngestionStatus] = useState<boolean>(false)

  useEffect(() => {
    loadIngestionStatus()
    testS3Connection()
    loadFileStats()
    loadCronJobs()
  }, [])

  useEffect(() => {
    if (ingestionStatus?.isRunning) {
      // Frequent status checks; file stats triggers full S3 listing — throttle to avoid starving ingestion
      const statusInterval = setInterval(() => {
        loadIngestionStatus()
      }, 5000)
      const statsInterval = setInterval(() => {
        loadFileStats()
      }, 30000)
      return () => {
        clearInterval(statusInterval)
        clearInterval(statsInterval)
      }
    }
  }, [ingestionStatus?.isRunning])

  useEffect(() => {
    if (previousIngestionStatus && ingestionStatus && !ingestionStatus.isRunning) {
      const lr = ingestionStatus.lastRun
      if (lr && !lr.success) {
        toast.error(lr.error || 'Data ingestion finished with errors.', {
          position: 'top-right',
          autoClose: 12000
        })
      } else {
        toast.success('Data ingestion completed successfully!', {
          position: 'top-right',
          autoClose: 7000
        })
      }
      loadFileStats()
    }
    if (ingestionStatus) {
      setPreviousIngestionStatus(ingestionStatus.isRunning)
    }
  }, [ingestionStatus?.isRunning, previousIngestionStatus])

  const loadIngestionStatus = async () => {
    try {
      const token = localStorage.getItem('finops_token')
      const headers = { Authorization: `Bearer ${token}` }
      const response = await axios.get('/api/ingestion/status', { headers })
      setIngestionStatus(response.data)
    } catch (error) {
      console.error('Failed to load ingestion status:', error)
    }
  }

  const loadFileStats = async () => {
    try {
      const token = localStorage.getItem('finops_token')
      const headers = { Authorization: `Bearer ${token}` }
      const response = await axios.get('/api/ingestion/files-stats', { headers })
      setFileStats(response.data)
    } catch (error) {
      console.error('Failed to load file stats:', error)
    }
  }

  const loadCronJobs = async () => {
    try {
      const token = localStorage.getItem('finops_token')
      const headers = { Authorization: `Bearer ${token}` }
      const response = await axios.get('/api/cron-jobs', { headers })
      setCronJobs(response.data.data || [])
    } catch (error) {
      console.error('Failed to load cron jobs:', error)
    }
  }

  const toggleCronJob = async (cronJobId: number) => {
    try {
      const token = localStorage.getItem('finops_token')
      const headers = { Authorization: `Bearer ${token}` }
      await axios.post(`/api/cron-jobs/${cronJobId}/toggle`, {}, { headers })
      await loadCronJobs()
    } catch (error) {
      console.error('Failed to toggle cron job:', error)
    }
  }

  const updateCronJob = async (cronJob: CronJob) => {
    try {
      const token = localStorage.getItem('finops_token')
      const headers = { Authorization: `Bearer ${token}` }
      await axios.put(
        `/api/cron-jobs/${cronJob.id}`,
        {
          name: cronJob.name,
          description: cronJob.description,
          cronExpression: cronJob.cronExpression,
          isEnabled: cronJob.isEnabled
        },
        { headers }
      )
      await loadCronJobs()
      setEditingCronJob(null)
    } catch (error) {
      console.error('Failed to update cron job:', error)
    }
  }

  const testS3Connection = async () => {
    try {
      const token = localStorage.getItem('finops_token')
      const headers = { Authorization: `Bearer ${token}` }
      const response = await axios.get('/api/ingestion/test-connection', { headers })
      setConnectionTest({ success: true, filesFound: response.data.filesFound })
    } catch (error: any) {
      setConnectionTest({
        success: false,
        error: error.response?.data?.error || error.message
      })
    }
  }

  const triggerIngestion = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('finops_token')
      const headers = { Authorization: `Bearer ${token}` }
      const response = await axios.post('/api/ingestion/ingest', {}, { headers })

      if (response.data.success) {
        toast.success('Full ingestion started! Processing all available files.')
        await loadIngestionStatus()
      } else {
        toast.error(response.data.error || 'Failed to start ingestion')
      }
    } catch (error: any) {
      if (error.response?.status === 409) {
        toast.warning('Ingestion is already in progress')
      } else {
        toast.error(error.response?.data?.error || 'Failed to start ingestion')
      }
    } finally {
      setLoading(false)
    }
  }

  const triggerDateRangeIngestion = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('finops_token')
      const headers = { Authorization: `Bearer ${token}` }

      const endDate = new Date()
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - 7)

      const startDateStr = startDate.toISOString().split('T')[0]
      const endDateStr = endDate.toISOString().split('T')[0]

      const response = await axios.post(
        '/api/ingestion/ingest-range',
        {
          startDate: startDateStr,
          endDate: endDateStr
        },
        { headers }
      )

      if (response.data.success) {
        toast.success(
          `Date range ingestion started! Processing files from ${startDateStr} to ${endDateStr}.`
        )
        await loadIngestionStatus()
      } else {
        toast.error(response.data.error || 'Failed to start date range ingestion')
      }
    } catch (error: any) {
      if (error.response?.status === 409) {
        toast.warning('Ingestion is already in progress')
      } else {
        toast.error(error.response?.data?.error || 'Failed to start date range ingestion')
      }
    } finally {
      setLoading(false)
    }
  }

  const enabledJobs = cronJobs.filter((job) => job.isEnabled).length

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Data Ingestion</h1>
        <p className="mt-1 text-sm text-gray-600">Manage AWS cost data import and processing</p>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                ingestionStatus?.isRunning ? 'bg-blue-100' : 'bg-gray-100'
              }`}
            >
              {ingestionStatus?.isRunning ? (
                <div className="loading-spinner w-6 h-6"></div>
              ) : (
                <svg
                  className="w-6 h-6 text-gray-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Status</p>
              <p
                className={`text-xl font-bold ${ingestionStatus?.isRunning ? 'text-blue-600' : 'text-gray-900'}`}
              >
                {ingestionStatus?.isRunning ? 'Running' : 'Idle'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                connectionTest?.success ? 'bg-green-100' : 'bg-red-100'
              }`}
            >
              <svg
                className={`w-6 h-6 ${connectionTest?.success ? 'text-green-600' : 'text-red-600'}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {connectionTest?.success ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                )}
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-500">S3 Connection</p>
              <p
                className={`text-xl font-bold ${connectionTest?.success ? 'text-green-600' : 'text-red-600'}`}
              >
                {connectionTest?.success ? 'Connected' : 'Failed'}
              </p>
              {connectionTest && !connectionTest.success && (
                <button
                  onClick={() => setShowErrorDetails(true)}
                  className="text-xs text-blue-600 hover:underline"
                >
                  View details
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-amber-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Files Processed</p>
              {fileStats ? (
                <>
                  <p className="text-xl font-bold text-gray-900">
                    {fileStats.processed} / {fileStats.totalS3Files}
                  </p>
                  {fileStats.pending > 0 && (
                    <p className="text-xs text-gray-500">Pending: {fileStats.pending}</p>
                  )}
                  {fileStats.failed > 0 && (
                    <p className="text-xs text-red-600">Failed: {fileStats.failed}</p>
                  )}
                </>
              ) : (
                <p className="text-xl font-bold text-gray-900">Loading...</p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-gray-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Scheduled Jobs</p>
              <p className="text-xl font-bold text-gray-900">
                {enabledJobs} / {cronJobs.length}
              </p>
              <p className="text-xs text-gray-500">{enabledJobs} active</p>
            </div>
          </div>
        </div>
      </div>

      {/* Ingestion Controls */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Data Ingestion</h2>
        </div>
        <div className="p-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex gap-3">
              <svg
                className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <h4 className="text-sm font-medium text-blue-800">
                  AWS Cost and Usage Reports (CUR)
                </h4>
                <p className="mt-1 text-sm text-blue-700">
                  Import cost data from your AWS S3 bucket. The system will automatically handle
                  pagination to process all available files.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={triggerIngestion}
              disabled={loading || ingestionStatus?.isRunning}
              className={`btn ${loading || ingestionStatus?.isRunning ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'btn-primary'}`}
            >
              {loading || ingestionStatus?.isRunning ? (
                <>
                  <div className="loading-spinner w-4 h-4 mr-2"></div>
                  {loading ? 'Starting...' : 'Processing...'}
                </>
              ) : (
                <>
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Import All Data
                </>
              )}
            </button>

            <button
              onClick={triggerDateRangeIngestion}
              disabled={loading || ingestionStatus?.isRunning}
              className={`btn ${loading || ingestionStatus?.isRunning ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'btn-secondary'}`}
            >
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              Import Last 7 Days
            </button>
          </div>
        </div>
      </div>

      {/* Scheduled Jobs */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Scheduled Jobs</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Job
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Schedule
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Run
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Result
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {cronJobs.map((job) => (
                <tr key={job.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{job.name}</div>
                    <div className="text-xs text-gray-500">{job.description}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <code className="text-sm text-gray-900 bg-gray-100 px-2 py-1 rounded">
                      {job.cronExpression}
                    </code>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${
                        job.isEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${job.isEnabled ? 'bg-green-500' : 'bg-gray-400'}`}
                      ></span>
                      {job.isEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {job.lastRunAt ? new Date(job.lastRunAt).toLocaleString() : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {job.lastStatus && (
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          job.lastStatus === 'success'
                            ? 'bg-green-100 text-green-700'
                            : job.lastStatus === 'failed'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {job.lastStatus}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-2">
                      <button
                        onClick={() => toggleCronJob(job.id)}
                        className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                          job.isEnabled
                            ? 'bg-red-50 text-red-700 hover:bg-red-100'
                            : 'bg-green-50 text-green-700 hover:bg-green-100'
                        }`}
                      >
                        {job.isEnabled ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        onClick={() => setEditingCronJob(job)}
                        className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors"
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {cronJobs.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    No scheduled jobs found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Cron Job Modal */}
      {editingCronJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setEditingCronJob(null)}
          ></div>
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Edit Scheduled Job</h3>
              <button
                onClick={() => setEditingCronJob(null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <svg
                  className="w-5 h-5 text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                updateCronJob(editingCronJob)
              }}
            >
              <div className="p-6 space-y-4">
                <div>
                  <label className="form-label">Job Name</label>
                  <input
                    type="text"
                    value={editingCronJob.name}
                    onChange={(e) => setEditingCronJob({ ...editingCronJob, name: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Description</label>
                  <textarea
                    value={editingCronJob.description}
                    onChange={(e) =>
                      setEditingCronJob({ ...editingCronJob, description: e.target.value })
                    }
                    className="form-input"
                    rows={2}
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Cron Expression</label>
                  <input
                    type="text"
                    value={editingCronJob.cronExpression}
                    onChange={(e) =>
                      setEditingCronJob({ ...editingCronJob, cronExpression: e.target.value })
                    }
                    className="form-input font-mono"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Example: 0 2 * * * (daily at 2 AM)</p>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Enable Job</span>
                  <button
                    type="button"
                    onClick={() =>
                      setEditingCronJob({ ...editingCronJob, isEnabled: !editingCronJob.isEnabled })
                    }
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      editingCronJob.isEnabled ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        editingCronJob.isEnabled ? 'left-6' : 'left-1'
                      }`}
                    ></span>
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
                <button
                  type="button"
                  onClick={() => setEditingCronJob(null)}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary flex-1">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Error Details Modal */}
      {showErrorDetails && connectionTest?.error && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowErrorDetails(false)}
          ></div>
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">S3 Connection Error</h3>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-700 font-mono break-all">{connectionTest.error}</p>
            </div>
            <div className="text-sm text-gray-600 mb-4">
              <p className="font-medium mb-2">Common solutions:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-500">
                <li>Check AWS credentials</li>
                <li>Verify S3 bucket name and region</li>
                <li>Ensure IAM permissions</li>
              </ul>
            </div>
            <button onClick={() => setShowErrorDetails(false)} className="btn btn-primary w-full">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
